import { CoinChip, Badge } from '../ui/components';
import { state, isPremiumActive } from '../store';
import { api, isHalted } from '../api/client';
import { t, getLocalized } from '../i18n';
import { sanitizeHtml } from '../utils/sanitize';
import { getNextMilestoneText } from '../utils/milestones';

async function ensureReferralForHome() {
  if (isHalted()) return;  // terminal auth state — no fetches
  if (!state.referralStats) {
    try {
      state.referralStats = await api.getReferral();
      if (isHalted()) return;  // became terminal while awaiting
      if (state.referralStats && typeof window.renderApp === 'function') window.renderApp();
    } catch(e) { /* silent, not critical */ }
  }
}

async function ensureGamesLoaded() {
  if (isHalted()) return;  // terminal auth state — no fetches
  if (state.games.length === 0) {
    try {
      const res: any = await api.getGames();
      state.games = res.games ?? res;
    } catch(e) {
      console.error('Failed to load games', e);
      throw e;
    }
  }
}

declare global {
  interface Window {
    openGameLevels: (gameId: string) => void;
    shareInviteFromHome: () => void;
  }
}

window.shareInviteFromHome = () => {
  const link = state.referralStats?.referral_link;
  if (!link) {
    window.navTo('invite');
    return;
  }
  const text = encodeURIComponent(t('invite_share_text'));
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${text}`;
  if ((window as any).Telegram?.WebApp?.openTelegramLink) {
    (window as any).Telegram.WebApp.openTelegramLink(shareUrl);
  } else {
    window.open(shareUrl, '_blank');
  }
};

window.openGameLevels = (gameId: string) => {
  if (gameId === 'activity') {
    window.navTo('tournament-setup');
    return;
  }

  const game = state.games.find(g => g.id === gameId);
  if (!game) return;
  const hasAccess = game.user_has_access || isPremiumActive();
  
  if (!hasAccess) {
    if (game.unlock_type === 'invite') {
        window.navTo('invite');
    } else if (game.unlock_type === 'premium') {
        window.navTo('premium');
    } else if (game.unlock_type === 'coin') {
        // Вызываем попап покупки. Передаем '__game__' как levelId
        if (typeof window.showUnlockPopup === 'function') {
            window.showUnlockPopup(
                sanitizeHtml(getLocalized(game.name)),
                sanitizeHtml(game.icon),
                game.unlock_cost || 0,
                '__game__',
                game.id
            );
        }
    }
  } else {
    if (typeof window.selectGameTab === 'function') {
      window.selectGameTab(gameId);
    }
    window.navTo('games');
  }
};

export function setupHomeListener(): void {
  const screen = document.getElementById('screen-home');
  if (!screen) return;
  screen.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-open-game]');
    if (!el) return;
    window.openGameLevels(el.dataset.openGame!);
  });
}

export function renderHome(): string {
  ensureReferralForHome(); // fire-and-forget

  const gameLayout = {
    activity: { bg: 'bg-[linear-gradient(135deg,#1a1400,#3d2200)] shadow-[0_8px_32px_rgba(255,215,0,0.25)]', badgeType: 'free', glow: 'bg-[radial-gradient(circle,rgba(255,215,0,0.6),transparent)]', delay: '-0.2s' },
    tod: { bg: 'bg-[linear-gradient(135deg,#1a0530,#2d0a4e)] shadow-[0_8px_32px_rgba(160,32,240,0.25)]', badgeType: 'free', glow: 'bg-[radial-gradient(circle,rgba(160,32,240,0.6),transparent)]', delay: '0s' },
    wai: { bg: 'bg-[linear-gradient(135deg,#051530,#0a284e)] shadow-[0_8px_32px_rgba(45,107,255,0.2)]', badgeType: 'free', glow: 'bg-[radial-gradient(circle,rgba(45,107,255,0.6),transparent)]', delay: '-0.5s' },
    nhie: { bg: 'bg-[linear-gradient(135deg,#1a0d00,#2d1800)] shadow-[0_8px_32px_rgba(255,140,0,0.18)]', badgeType: 'hit', glow: 'bg-[radial-gradient(circle,rgba(255,140,0,0.6),transparent)]', delay: '-1s' },
    describe: { bg: 'bg-[linear-gradient(135deg,#0a2010,#143320)] shadow-[0_8px_32px_rgba(57,255,20,0.15)]', badgeType: 'coin', glow: 'bg-[radial-gradient(circle,rgba(57,255,20,0.5),transparent)]', delay: '-1.5s' },
    act: { bg: 'bg-[linear-gradient(135deg,#300515,#4e0a28)] shadow-[0_8px_32px_rgba(255,45,107,0.2)]', badgeType: 'coin', glow: 'bg-[radial-gradient(circle,rgba(255,45,107,0.6),transparent)]', delay: '-2s' },
    mlt: { bg: 'bg-[linear-gradient(135deg,#001a20,#002830)] shadow-[0_8px_32px_rgba(0,229,255,0.15)]', badgeType: 'free', glow: 'bg-[radial-gradient(circle,rgba(0,229,255,0.5),transparent)]', delay: '-2.5s' },
    wyr: { bg: 'bg-[linear-gradient(135deg,#1a1400,#2d2200)] shadow-[0_8px_32px_rgba(255,215,0,0.15)]', badgeType: 'free', glow: 'bg-[radial-gradient(circle,rgba(255,215,0,0.5),transparent)]', delay: '-0.8s' },
    emoji: { bg: 'bg-[linear-gradient(135deg,#1a0305,#2d0508)] shadow-[0_8px_32px_rgba(255,23,68,0.18)]', badgeType: 'coin', glow: 'bg-[radial-gradient(circle,rgba(255,23,68,0.5),transparent)]', delay: '-1.8s' }
  };

  let gamesContentHtml = '';

  if (!state.games || state.games.length === 0) {
    // Show 8 skeletons and trigger load. renderApp() is called only on success;
    // on failure ensureGamesLoaded() throws, .then() is skipped, no loop.
    ensureGamesLoaded()
      .then(() => { if (!isHalted() && typeof window.renderApp === 'function') window.renderApp(); })
      .catch(() => { /* error screens are already rendered by the API client */ });
    
    gamesContentHtml = Array.from({ length: 8 }).map(() => `
      <div class="bg-white/5 rounded-[18px] min-h-[88px] flex flex-row items-center animate-pulse">
        <div class="w-[25%] flex items-center justify-center py-4">
          <div class="w-[44px] h-[44px] rounded-full bg-white/10"></div>
        </div>
        <div class="flex-1 py-[14px] pr-[14px]">
          <div class="w-3/4 h-[16px] bg-white/10 rounded mb-[8px]"></div>
          <div class="w-1/2 h-[11px] bg-white/10 rounded mb-[16px]"></div>
          <div class="w-[60px] h-[22px] bg-white/10 rounded-pill ml-auto"></div>
        </div>
      </div>
    `).join('');
  } else {
    gamesContentHtml = state.games.map((game) => {
      const layout = gameLayout[game.id as keyof typeof gameLayout] ?? {
        bg: 'bg-glass shadow-[0_8px_32px_rgba(255,255,255,0.05)]',
        badgeType: 'free',
        glow: 'bg-[radial-gradient(circle,rgba(255,255,255,0.3),transparent)]',
        delay: '0s',
      };
      
      let badgeHtml = '';
      const unlockType = game.unlock_type || 'free';
      const hasAccess = game.user_has_access || isPremiumActive();
      const isLocked = !hasAccess;
      
      if (unlockType === 'invite') {
        if (isLocked) badgeHtml = Badge({ text: t('badge_invite').replace('{n}', String(game.unlock_cost || 10)), icon: '🤝', variant: 'invite' });
        else badgeHtml = Badge({ text: t('badge_open'), variant: 'free' });
      } else if (unlockType === 'premium') {
        if (isLocked) badgeHtml = Badge({ text: t('badge_premium'), icon: '👑', variant: 'premium' });
        else badgeHtml = Badge({ text: t('badge_open'), variant: 'free' });
      } else if (unlockType === 'coin') {
        if (isLocked) badgeHtml = Badge({ text: String(game.unlock_cost || 100), icon: '🪙', variant: 'coin' });
        else badgeHtml = Badge({ text: t('badge_open'), variant: 'free' });
      } else {
        if (isLocked) {
          if (layout.badgeType === 'free') {
            badgeHtml = Badge({ text: t('badge_free'), variant: 'free' });
          } else if (layout.badgeType === 'hit') {
            badgeHtml = Badge({ text: 'HIT', variant: 'free' });
          } else {
            badgeHtml = Badge({ text: '50', icon: '🪙', variant: 'coin' });
          }
        } else {
          badgeHtml = Badge({ text: t('badge_free'), variant: 'free' });
        }
      }

      const lockHtml = isLocked
        ? `<div class="absolute inset-0 flex items-center justify-center text-[32px] pointer-events-none z-[2] opacity-20 animate-[lockFloat_3s_ease-in-out_infinite]">🔒</div>`
        : '';

      const cardOpacityClass = isLocked ? 'pointer-events-auto' : '';

      return `
        <div class="${layout.bg} rounded-[18px] cursor-pointer relative overflow-hidden min-h-[88px] flex flex-row items-stretch transition-all duration-[180ms] ease-out border border-white/5 active:scale-95 ${cardOpacityClass}" data-open-game="${sanitizeHtml(game.id)}">
          <div class="${layout.glow} absolute -top-[40px] -right-[40px] w-[120px] h-[120px] rounded-full blur-[30px] opacity-40 pointer-events-none"></div>
          <!-- Emoji 25% -->
          <div class="w-[25%] flex items-center justify-center relative ${isLocked ? 'opacity-50' : ''}">
            ${lockHtml}
            <div class="text-[44px] animate-[iconFloat_3s_ease-in-out_infinite] leading-none" style="animation-delay: ${layout.delay};">${game.icon}</div>
          </div>
          <!-- Content 75% -->
          <div class="flex-1 flex flex-col justify-between py-[14px] pr-[14px] ${isLocked ? 'opacity-50' : ''}">
            <div class="relative z-[3]">
              <div class="font-display text-[16px] font-extrabold text-white leading-[1.2]">${sanitizeHtml(getLocalized(game.name))}</div>
              <div class="text-[12px] text-white/50 mt-[4px] font-semibold leading-snug">${sanitizeHtml(getLocalized(game.desc))}</div>
            </div>
            <div class="flex justify-end mt-[8px]">
              ${badgeHtml}
            </div>
          </div>
        </div>
      `;
    }).join('');
  }


  return `
    <div class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[96px] opacity-100 pointer-events-auto transform translate-y-0 z-10 scrollbar-none bg-transparent" id="screen-home">
      
      <!-- Header -->
      <div class="flex items-center justify-between pt-[20px] px-[20px] pb-0">
        <div>
          <div class="text-[13px] text-t-secondary font-semibold">${t('home_greeting')}</div>
          <div class="font-display text-[26px] font-extrabold text-t-primary">${sanitizeHtml(state.user?.first_name || 'Player')} 🎉</div>
        </div>
        <div class="flex items-center gap-[6px]">
          ${CoinChip({ amount: state.user?.coins ?? 0, onClick: "nav-premium-coins" })}
          ${isPremiumActive() ? `<div class="flex items-center gap-[4px] bg-neon-gold/[0.15] border border-neon-gold/[0.3] rounded-pill py-[5px] px-[10px]">
            <span class="text-[13px]">👑</span>
            <span class="text-[11px] font-extrabold text-neon-gold">PRO</span>
          </div>` : ''}
        </div>
      </div>

      <!-- Premium CTA Banner -->
      ${!isPremiumActive() ? `<div class="mx-[16px] mt-[14px] p-[18px] rounded-[24px] border border-neon-gold/25 cursor-pointer relative overflow-hidden transition-all duration-150 active:scale-[0.98] bg-[linear-gradient(135deg,rgba(255,215,0,0.12),rgba(255,140,0,0.08),rgba(255,45,107,0.08))]" data-action="nav" data-to="premium">
        <div class="absolute inset-0 pointer-events-none overflow-hidden rounded-[24px]">
          <div class="absolute inset-0 -translate-x-full animate-[shimmer_2s_ease-in-out_infinite]" style="background: linear-gradient(90deg, transparent 0%, rgba(255,215,0,0.18) 50%, transparent 100%); border-radius: inherit;"></div>
        </div>
        <div class="flex items-center gap-[12px] relative z-10">
          <span class="text-[32px]">👑</span>
          <div class="flex-1">
            <div class="text-[15px] font-extrabold text-neon-gold">${t('home_try_premium')}</div>
            <div class="text-[12px] text-t-secondary mt-[2px] font-semibold">${t('home_premium_sub')}</div>
          </div>
          ${Badge({ text: t('badge_premium'), variant: 'premium' })}
        </div>
      </div>` : `
      <div class="mx-[16px] mt-[14px] p-[14px_16px] rounded-[24px] border border-neon-gold/20 bg-neon-gold/[0.06] flex items-center gap-[12px]">
        <span class="text-[28px]">👑</span>
        <div>
          <div class="text-[14px] font-extrabold text-neon-gold">${t('home_premium_active')}</div>
          <div class="text-[12px] text-t-secondary font-semibold">${t('home_premium_active_sub')}</div>
        </div>
      </div>`}

      <!-- Section: Game Modes -->
      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[12px] px-[20px] mt-[20px]">${t('home_game_modes')}</div>
      <div class="flex flex-col gap-[10px] px-[16px]">
        ${gamesContentHtml}
      </div>

      <!-- Invite Banner -->
      ${(() => {
        const stats = state.referralStats;
        const totalInvited = stats?.total_invited ?? 0;
        const allMilestones = stats?.milestones ?? [];
        const nextMilestone = allMilestones.find((m: any) => !m.claimed);
        const progressMax = nextMilestone
          ? nextMilestone.count
          : (allMilestones[allMilestones.length - 1]?.count ?? totalInvited);
        const progressPercent = Math.min(100, (totalInvited / Math.max(1, progressMax)) * 100);

        const nextMilestoneText = allMilestones.length > 0 ? getNextMilestoneText(totalInvited, allMilestones) : '';

        return `
          <div class="mx-[16px] mt-[14px] mb-[4px] rounded-[24px] relative overflow-hidden"
               style="background:linear-gradient(135deg,rgba(45,107,255,0.22),rgba(0,229,255,0.12));border:1.5px solid rgba(0,229,255,0.35);box-shadow:0 0 24px rgba(45,107,255,0.18),inset 0 0 40px rgba(0,229,255,0.04)">
            <div class="absolute inset-0 pointer-events-none overflow-hidden rounded-[24px]">
              <div class="absolute inset-0 -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]" style="background:linear-gradient(90deg,transparent 0%,rgba(0,229,255,0.1) 50%,transparent 100%);border-radius:inherit"></div>
            </div>
            <div class="p-[20px] relative z-10">
              <div class="flex items-end gap-[6px] mb-[4px]">
                <div class="font-display text-[44px] font-extrabold text-white leading-none">${totalInvited}</div>
                <div class="mb-[8px] text-[20px] text-white/30">/${progressMax}</div>
              </div>
              <div class="text-[13px] text-t-secondary font-semibold mb-[12px]">${t('invite_friends_count')}</div>
              <div class="h-[8px] bg-white/[0.08] rounded-pill overflow-hidden mb-[10px]">
                <div class="h-full rounded-pill bg-grad-blue shadow-[0_0_10px_rgba(45,107,255,0.4)] transition-all duration-500" style="width:${progressPercent}%"></div>
              </div>
              <div class="text-[13px] font-bold text-neon-cyan mb-[16px]">${nextMilestoneText}</div>
              <button
                class="flex items-center justify-center gap-[8px] w-full py-[16px] px-[24px] bg-grad-blue shadow-[0_0_20px_rgba(45,107,255,0.5)] rounded-pill text-white font-body text-[17px] font-extrabold active:scale-[0.98] transition-all duration-150"
                data-action="share-invite-home"
              >
                ${t('invite_btn')}
              </button>
              <div class="flex items-center justify-center gap-[6px] mt-[12px] p-[8px_16px] bg-neon-gold/[0.1] border border-neon-gold/[0.2] rounded-[12px]">
                <span class="text-[16px]">🪙</span>
                <span class="text-[13px] font-extrabold text-neon-gold">${t('rewards_per_friend_coins')}</span>
              </div>
            </div>
          </div>
        `;
      })()}

      <div class="h-[24px]"></div>
    </div>
  `;
}
